/**
 * `Ease: EaseApi` facade (contract src/core/ease/types.ts) and the LINEAR / HOLD constants.
 *
 * Every curve computation reproduces Phira in f32 emulation (Math.fround after each step): tweens (tweens.ts),
 * easingType resolution (rpeMap.ts), clamped slices (clamped.ts), the bezier solver and cache key (bezier.ts,
 * bezierKey.ts), the 170-integrator integrals and derivative (integrals.ts, derivative.ts), the text tween
 * (stringTween.ts). Editing helpers: split (split.ts), reverse (reverse.ts), catalogue and search (catalog.ts),
 * bezier fits (approx.ts), use guards (guards.ts). Reimplemented from docs/research; no Phira code is copied.
 */

import { approxBezier } from './approx';
import { bezierY } from './bezier';
import { bezierKey } from './bezierKey';
import { catalogEntry, searchEasings } from './catalog';
import { clampedY, sliceApplies, sliceIsDegenerate } from './clamped';
import { derivative } from './derivative';
import { overshoots, validateFor } from './guards';
import { generalIntY, intClampedY, intStaticY } from './integrals';
import { reverse } from './reverse';
import { resolveRpe, rpeToTweenId, ZERO_BEZIER } from './rpeMap';
import { splitAt } from './split';
import { stringTween } from './stringTween';
import { staticY } from './tweens';
import type { EaseApi, Ease as EaseValue, PhiraTweenDesc } from './types';

/** Linear (RPE easingType 1) over the whole curve. */
export const LINEAR: EaseValue = { kind: 'tween', id: 1, left: 0, right: 1 };
/** Stay, then jump. */
export const HOLD: EaseValue = { kind: 'hold' };

function phiraY(desc: PhiraTweenDesc, x: number): number {
  switch (desc.kind) {
    case 'static':
      return staticY(desc.tween, x);
    case 'clamped':
      return clampedY(desc.tween, desc.l, desc.r, x);
    case 'bezier':
      return bezierY(desc.p, x);
  }
}

function modelY(e: EaseValue, x: number): number {
  switch (e.kind) {
    case 'hold':
      return 0;
    case 'tween':
      return phiraY(resolveRpe(e.id, e.left, e.right, 0, ZERO_BEZIER), x);
    case 'bezier':
      return bezierY(e.p, x);
  }
}

export const Ease: EaseApi = {
  staticY,
  rpeToTweenId,
  resolveRpe,
  clampedY,
  bezierY,
  bezierKey,
  phiraY,
  modelY,
  intStaticY,
  intClampedY,
  generalIntY,
  derivative,
  stringTween,
  overshoots,
  sliceApplies,
  sliceIsDegenerate,
  splitAt,
  reverse,
  approxBezier,
  catalog: catalogEntry,
  search: searchEasings,
  validateFor,
  LINEAR,
  HOLD,
};

export { EASING_CATALOG } from './catalog';
